import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  PRODUCT_TYPE_COLOR,
  PRODUCT_TYPE_LABEL,
  REPORT_STATUS_COLOR,
  REPORT_STATUS_LABEL,
} from "./data";
import {
  SalesReportItem,
  SalesReportSummary,
} from "../../../types/sale-reports.type";
import { useNavigate, useParams } from "react-router-dom";
import { getSaleReportItems } from "../../../services";
import { usePdfDownload } from "../../../utils/hooks/usePdfDownload";
import Grid from "@mui/material/Grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { NARROW_QUERY } from "../../../utils/hooks/useCompact";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";
import SaleTargetCard from "../../../components/Report/CardReport/SaleTargetCard";
import SalePersonCard from "../../../components/Report/CardReport/SalePersonCard";
import TripDataCard from "../../../components/Report/CardReport/TripDataCard";
import TripOverviewCard from "../../../components/Report/CardReport/TripOverviewCard";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import ReportTableSkeleton from "./ReportTableSkeleton";
import { formatNumber } from "../../../utils";

interface ReportDetailsListProps {
  reportSummary: SalesReportSummary;
  versionHistory: string;
  setTriggerFetch: Dispatch<SetStateAction<number>>;
}

export default function ReportDetailsList({
  reportSummary,
  versionHistory,
  setTriggerFetch,
}: ReportDetailsListProps) {
  const navigate = useNavigate();
  const { downloadingId, downloadPdf } = usePdfDownload();
  const { reportId } = useParams<{ reportId: string }>();
  const isNarrow = useMediaQuery(NARROW_QUERY, { noSsr: true });
  const [reportItems, setReportItems] = useState<SalesReportItem[]>();

  const onGetSaleReportItems = useCallback(
    async (uuid: string, version: string) => {
      const {
        data: { data },
      } = await getSaleReportItems(uuid, version);
      setReportItems(data);
    },
    [],
  );

  useEffect(() => {
    if (reportId && versionHistory) {
      onGetSaleReportItems(reportId, versionHistory);
    }
  }, [reportId, versionHistory, onGetSaleReportItems]);

  if (!reportItems) return <ReportTableSkeleton />;

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={isNarrow ? 6 : 2}>
          <SaleTargetCard
            reportSummary={reportSummary}
            setTriggerFetch={setTriggerFetch}
          />
        </Grid>
        <Grid size={isNarrow ? 6 : 3}>
          <SalePersonCard reportSummary={reportSummary} />
        </Grid>
        <Grid size={isNarrow ? 6 : 3}>
          <TripDataCard reportSummary={reportSummary} />
        </Grid>
        <Grid size={isNarrow ? 6 : 4}>
          <TripOverviewCard reportSummary={reportSummary} />
        </Grid>
      </Grid>
      <TableContainer sx={{ borderRadius: "12px", mt: "24px" }}>
        <Table>
          <TableHead
            sx={{
              bgcolor: (theme) => theme.palette.background.default,
            }}
          >
            <TableRow>
              <TableCell
                rowSpan={2}
                align="left"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ลำดับ
              </TableCell>
              <TableCell
                rowSpan={2}
                align="left"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ชื่อร้าน (Store Name)
              </TableCell>
              <TableCell
                rowSpan={2}
                align="left"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                รหัสร้าน (CustNo)
              </TableCell>
              <TableCell
                rowSpan={2}
                align="left"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                เวลาเข้า
              </TableCell>
              <TableCell
                rowSpan={2}
                align="left"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ระยะเวลา
              </TableCell>
              <TableCell
                rowSpan={2}
                align="left"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                เวลาออก
              </TableCell>
              <TableCell
                rowSpan={2}
                align="left"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                บิล (Invoice)
              </TableCell>
              <TableCell
                rowSpan={2}
                align="left"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ยอดพิมพ์ใบเสร็จ
              </TableCell>
              <TableCell
                rowSpan={2}
                align="left"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ของเหลือ
              </TableCell>
              <TableCell
                rowSpan={2}
                align="left"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ของเสีย
              </TableCell>
              <TableCell
                colSpan={2}
                align="center"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                มูลค่าสินค้า
              </TableCell>
              <TableCell
                rowSpan={2}
                align="center"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ภาษี
              </TableCell>
              <TableCell
                rowSpan={2}
                align="center"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                รวมยอดขาย
              </TableCell>
              <TableCell
                rowSpan={2}
                align="center"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                สถานะ
              </TableCell>
              <TableCell rowSpan={2} align="center">
                PDF
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                align="center"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ประเภท
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                จำนวน
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reportItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={16} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูล
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {reportItems.length > 0 &&
              reportItems.map((report, index) => {
                const isCancel = report.bill_status === "cancel";
                const isInvoice = report.bill_type === "invoice";

                const url = isInvoice
                  ? `/sales/invoice/details/${report?.bill_uuid || ""}`
                  : `/sales/cash-sales/details/${report?.bill_uuid || ""}`;

                return (
                  <TableRow
                    key={report.bill_id}
                    sx={{
                      cursor: "pointer",
                      transition: "background 0.1s",
                      "& td, & th": { padding: "8px" },
                      bgcolor: isCancel
                        ? (theme) => theme.palette.error.main + "22"
                        : index % 2 === 1
                          ? "#f6f6f9"
                          : "#fff",
                    }}
                    onClick={() => navigate(url)}
                  >
                    <TableCell align="center" sx={{ fontWeight: 500 }}>
                      {report.isComment && (
                        <Box sx={{ display: "flex" }}>
                          <Box
                            component="img"
                            src="/icons/warning-icon.png"
                            width={24}
                            height={24}
                          />
                          <Typography sx={{ ml: 1 }}>{index + 1}</Typography>
                        </Box>
                      )}
                      {!report.isComment && (
                        <Typography sx={{ ml: 1 }}>{index + 1}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="left" sx={{ fontWeight: 500 }}>
                      {report.customer_name || "-"}
                    </TableCell>
                    <TableCell align="center">{report.customer_code}</TableCell>
                    <TableCell align="center">{report.start_time}</TableCell>
                    <TableCell align="center">{report.duration_time}</TableCell>
                    <TableCell align="center">{report.end_time}</TableCell>
                    <TableCell align="center">
                      <Link
                        sx={{
                          color: "#5C69E8",
                          fontWeight: 500,
                          textDecoration: "underline",
                        }}
                      >
                        {report.bill_id}
                      </Link>
                    </TableCell>
                    <TableCell align="center">{report.printed}</TableCell>
                    <TableCell align="center">
                      {report.leftover_count}
                    </TableCell>
                    <TableCell align="center">{report.waste_count}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={PRODUCT_TYPE_LABEL[report.bill_type]}
                        variant="outlined"
                        size="small"
                        sx={{
                          width: "100%",
                          height: "36px",
                          borderColor: PRODUCT_TYPE_COLOR[report.bill_type],
                          color: PRODUCT_TYPE_COLOR[report.bill_type],
                          bgcolor: PRODUCT_TYPE_COLOR[report.bill_type] + "1A",
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {formatNumber(report.amount_total)}
                    </TableCell>
                    <TableCell align="center">
                      {formatNumber(report.vat_total)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        color: isCancel
                          ? (theme) => theme.palette.error.main
                          : "#1a1a1a",
                      }}
                    >
                      {formatNumber(report.net_total)}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            flexShrink: 0,
                            borderRadius: "50%",
                            bgcolor: REPORT_STATUS_COLOR[report.bill_status],
                          }}
                        />
                        <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                          {REPORT_STATUS_LABEL[report.bill_status]}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="ดาวน์โหลด PDF">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={!report.bill_uuid || downloadingId !== null}
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadPdf(report.bill_uuid);
                            }}
                          >
                            {downloadingId === report.bill_uuid ? (
                              <CircularProgress size={18} />
                            ) : (
                              <DownloadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
